'use client';

import type { ReactNode } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { useIsAuthed } from '@/hooks/useIsAuthed';
import { useLocalTranslation } from '@/hooks/useLocalTranslation';
import { useAuthStore } from '@/stores/authStore';
import { useRawInitDataReady } from '@/hooks/useRawInitDataWithDev';
import { useServerHasAuth } from '@/contexts/ServerAuthContext';
import { ScreenContent, ScreenTabBar } from '@/screen';
import { LoginMethods } from '@/components/LoginMethods';
import translations from './RequiresLogin.translations';

export type RequiresLoginScreen =
  | 'profile'
  | 'upgrade'
  | 'contracts'
  | 'inventory'
  | 'referrals'
  | 'payment'
  | 'caseOpen'
  | 'caseBattleCreate'
  | 'rushMid'
  | 'rushMidSurvey'
  | 'roulette';

interface RequiresLoginProps {
  children: ReactNode;
  /** Which copy to show on the wall. Each screen key has its own
   * loginTitle/loginReason in RequiresLogin.translations.ts. */
  screen: RequiresLoginScreen;
}

/**
 * Per-screen login wall: when the visitor is anonymous, render a
 * polite "log in to continue" panel instead of letting the screen
 * crash on missing user data. When authed, transparently render
 * children.
 *
 * SSR uses `useServerHasAuth()` (cookie sniff in (app)/layout) so
 * the first paint matches the post-hydration state — no
 * "skeleton flash → real content / login wall" jump on every
 * refresh. After client hydration, live auth state from authStore +
 * initData takes over (e.g. token swap mid-session).
 */
export function RequiresLogin({ children, screen }: RequiresLoginProps) {
  const isAuthed = useIsAuthed();
  const hydrated = useAuthStore((s) => s.hydrated);
  const initDataReady = useRawInitDataReady();
  const serverHasAuth = useServerHasAuth();
  const { t } = useLocalTranslation(translations);
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const showChildren = (!hydrated || !initDataReady) ? serverHasAuth : isAuthed;
  if (showChildren) return <>{children}</>;

  const search = searchParams?.toString();
  const next = (pathname ?? '/') + (search ? `?${search}` : '');

  const titleKey = `${screen}Title` as keyof typeof translations.en;
  const reasonKey = `${screen}Reason` as keyof typeof translations.en;

  return (
    <>
      <ScreenTabBar />
      <ScreenContent>
        <div className="min-h-full flex flex-col items-center justify-center px-6 py-10 text-center">
          <div className="text-2xl font-semibold text-white mb-3">{t(titleKey)}</div>
          <p className="text-base text-white/70 mb-8 max-w-[320px] leading-snug">{t(reasonKey)}</p>
          <LoginMethods next={next} />
        </div>
      </ScreenContent>
    </>
  );
}
